/** @jsxImportSource @emotion/react */
import React, { useCallback } from "react";
import "./SwapAndSend";
import { AddressInput } from "./elements/AddressInput";
import { SwapAndSendError } from "./elements/SwapAndSendError";
import { GlobalStore } from "../../store/global/GlobalStore";
import { PasteAddress } from "./elements/PasteAddress";
import { ClearReceiver } from "./elements/ClearReciever";
import { fetchEnsAvatar, fetchEnsName } from "wagmi/actions";
import * as Sentry from "@sentry/browser";
import { isEthereumNetwork } from "../../utils/chainUtils";
import { SwapAndSendWarning } from "./elements/SwapAndSendWarning";
import { fetchEnsAddress } from "@wagmi/core";
import { sendSectionStyle } from "./SwapAndSendStyle";
import {
  INITIAL_RECEIVER_STATE,
  isValidChecksumAddress,
  resetReceiver,
} from "../../utils/swapAndSendUtils";
import { ViewOnExplorer, ViewOnExplorerDisplayLocations } from "./elements/ViewOnExplorer";
import { verticalAlignedFlexbox } from "../../config/commonCSS";
import AnimatedDropdownContent, { AnimationState } from "../AnimatedDropdown/AnimatedDropdown";
import { keyframes } from "@emotion/react";

export interface SendToSectionProps {
  visible?: boolean;
  animationState: AnimationState;
}

export const SwapAndSend: React.FC<SendToSectionProps> = ({ animationState }) => {
  const network = GlobalStore.useState(s => s.network);
  const address = GlobalStore.useState(s => s.address);
  const ensName = GlobalStore.useState(s => s.ensName);
  const receiver = GlobalStore.useState(s => s.receiver);
  const quote = GlobalStore.useState(s => s.quote);
  const receiverError = GlobalStore.useState(s => s.receiverError);
  const isValidAddress = isValidChecksumAddress(receiver.address);

  const ensValidation = (inputValue: string) => {
    fetchEnsAddress({
      name: inputValue,
    })
      .then(ensAddress => {
        if (ensAddress === null) {
          GlobalStore.update(s => {
            s.receiverError = "This address doesn't look right. Check it and try again.";
          });
        } else {
          GlobalStore.update(s => {
            s.receiverError = "";
          });
        }
      })
      .catch(err => {
        Sentry.captureException(
          `Failed to resolve ens name for swap and send when user try's ENS name`
        );
        GlobalStore.update(s => {
          s.receiverError = "This address doesn't look right. Check it and try again.";
        });
        return false;
      });
  };

  const validateInput = useCallback(
    (inputValue: string) => {
      const validChecksumAddress = isValidChecksumAddress(inputValue) && inputValue !== address;
      const validEns = inputValue !== ensName;

      if (inputValue === "") {
        resetReceiver();
      } else if (inputValue.endsWith(".eth") && isEthereumNetwork(network) && validEns) {
        ensValidation(inputValue);
      } else if (!validChecksumAddress) {
        const error =
          address === inputValue || ensName === inputValue
            ? "That's your address, please fix and try again."
            : "This address doesn't look right. Check it and try again.";
        GlobalStore.update(s => {
          s.receiverError = error;
        });
      }

      if (inputValue.endsWith(".eth") && isEthereumNetwork(network) && validEns) {
        ensValidation(inputValue);
      }
    },
    [network, address, ensName]
  );
  const setReceiverAvatar = (inputValue: string) => {
    GlobalStore.update(s => {
      s.receiver.userInput = inputValue;
      s.receiver.isAddressLoading = true;
    });
    fetchEnsAddress({
      name: inputValue,
    })
      .then(ensAddress => {
        if (ensAddress) {
          GlobalStore.update(s => {
            s.receiver.address = ensAddress;
            s.receiver.userInput = inputValue;
            s.receiver.isAddressLoading = false;
          });
          fetchEnsName({ address: ensAddress })
            .then(ensName => {
              if (ensName) {
                GlobalStore.update(s => {
                  s.receiver.ensName = inputValue;
                });
                fetchEnsAvatar({
                  name: ensName,
                })
                  .then(avatarUrl => {
                    if (avatarUrl) {
                      GlobalStore.update(s => {
                        s.receiver.avatar = avatarUrl;
                        s.receiver.isAddressLoading = false;
                      });
                    }
                  })
                  .catch(err => {
                    return;
                  });
              }
            })
            .catch(err => {
              //TODO: Do we need to flesh this out?
              return;
            });
        }
      })
      .catch(err => {
        return;
      });
  };

  const handleOnInput = useCallback(
    (value: string) => {
      validateInput(value);
      if (value !== "") {
        if (value.endsWith(".eth") && isEthereumNetwork(network)) {
          setReceiverAvatar(value);
        } else {
          GlobalStore.update(s => {
            s.receiver.address = value;
            s.receiver.isAddressLoading = false;
          });
        }
      }
    },
    [validateInput, network]
  );

  const handlePaste = useCallback(() => {
    navigator.clipboard
      .readText()
      .then(clipboardData => {
        validateInput(clipboardData);
        GlobalStore.update(s => {
          s.receiver.userInput = clipboardData;
          s.receiver.address = clipboardData;
        });
        handleOnInput(clipboardData);
      })
      .catch(error => {
        console.error("Failed to read clipboard data: ", error);
      });
  }, [validateInput, handleOnInput]);

  return (
    <AnimatedDropdownContent
      animationState={animationState}
      duration={250}
      openingAnimation={openingSwapAndSendAnimation}
      closingAnimation={closingSwapAndSendAnimation}
    >
      <div css={sendSectionStyle}>
        <div css={verticalAlignedFlexbox}>
          <AddressInput
            visible={true}
            handleOnInput={inputValue => handleOnInput(inputValue)}
            receiver={receiver}
          />
          <div css={verticalAlignedFlexbox}>
            <ViewOnExplorer
              visible={isValidAddress}
              address={quote?.receiver ?? receiver.address}
              displayLocation={ViewOnExplorerDisplayLocations.Entry}
            />
            <ClearReceiver visible={receiver !== INITIAL_RECEIVER_STATE} />
          </div>
          <PasteAddress visible={receiver === INITIAL_RECEIVER_STATE} handlePaste={handlePaste} />
        </div>
      </div>
      <SwapAndSendError visible={receiverError !== ""} />
      <SwapAndSendWarning visible={!receiverError} />
    </AnimatedDropdownContent>
  );
};

// Animation
export const openingSwapAndSendAnimation = keyframes`
  0% {
    opacity: 0.5;
    max-height: 0;
  }
  100% {
    opacity: 1;
    max-height: 450px;
  }
`;

export const closingSwapAndSendAnimation = keyframes`
  0% {
    opacity: 1;
    max-height: 450px;
  }
  100% {
    opacity: 0.5;
    max-height: 0;
  }
`;
